import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Close as CloseIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Replay as ReplayIcon,
} from "@mui/icons-material";
import { useClinicTheme } from "../../../theme";
import { COLORS, themedInputSx } from "../../../styles/uiConstants";
import FilterTabs from "./FilterTabs";
import ListSearchInput from "./ListSearchInput";

/**
 * `searchFields` girdisi metin (varsayılan) yada `type: "select"` olabilir.
 * Select, `ListSearchInput` ile aynı ölçekte (`size="small"`) ve aynı kenarlık
 * tedavisiyle (`themedInputSx`) çizilir; yüzen `label` yerine `ListSearchInput`
 * ile tutarlı, yer tutucu metni (muted renkte) gösteren `displayEmpty` deseni
 * kullanılır. `onChange` yine string alır. Masaüstü satırında ve mobil taslak
 * modalında ortak kullanılır.
 */
function ListSelectField({ placeholder, options = [], value, onChange, sx = {} }) {
  const { palette } = useClinicTheme();

  return (
    <TextField
      select
      size="small"
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      inputProps={{ "aria-label": placeholder }}
      SelectProps={{
        displayEmpty: true,
        renderValue: (selected) => {
          if (!selected) {
            return (
              <Box component="span" sx={{ color: COLORS.slate400 }}>
                {placeholder}
              </Box>
            );
          }
          const opt = options.find((o) => o.value === selected);
          return opt ? opt.label : selected;
        },
      }}
      sx={{ ...themedInputSx(palette), width: { xs: "100%", sm: 200 }, ...sx }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value || "all"} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function optionLabel(field) {
  const opt = (field.options || []).find((o) => o.value === field.value);
  return opt ? opt.label : field.value;
}

/**
 * Liste sayfalarının üst barı.
 *
 * Masaüstü (md+), üç satır:
 *   1) ikon + başlık + alt başlık — sağda sayaç ve aksiyon butonları
 *   2) FilterTabs
 *   3) arama alanları — sağda "Temizle"
 *
 * Mobil (md altı):
 *   Filtre ve arama satır içinde GÖSTERİLMEZ; filtre ikonuyla açılan modala
 *   taşınır. Modal TASLAK state ile çalışır — "Uygula"ya basılana kadar hiçbir
 *   onChange tetiklenmez, yani liste boşuna filtrelenmez. Uygulanmış filtreler
 *   toolbar altında silinebilir chip'ler olarak görünür. Aksiyonlar ⋮ menüsünde.
 *
 * Bölümlerin tamamı opsiyoneldir.
 */
export default function ListToolbar({
  icon,
  title,
  subtitle,
  count,
  countLabel,
  isLoading = false,
  actionItems = [],
  filterTabs,
  searchFields = [],
  onClear,
}) {
  const { palette } = useClinicTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [filterOpen, setFilterOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [draftFilter, setDraftFilter] = useState(null);
  const [draftValues, setDraftValues] = useState({});

  const hasFilterTabs = Boolean(filterTabs?.filters?.length);
  const hasSearch = searchFields.length > 0;
  const hasFilterContent = hasFilterTabs || hasSearch;
  const defaultFilterId = hasFilterTabs ? filterTabs.filters[0].id : null;

  const activeSearchFields = searchFields.filter((f) => f.value);
  const activeFilterDef =
    hasFilterTabs && filterTabs.activeFilter !== defaultFilterId
      ? filterTabs.filters.find((f) => f.id === filterTabs.activeFilter)
      : null;
  const hasActiveFilters = Boolean(activeFilterDef) || activeSearchFields.length > 0;

  const openFilterDialog = () => {
    setDraftFilter(hasFilterTabs ? filterTabs.activeFilter : null);
    setDraftValues(
      searchFields.reduce((acc, f) => ({ ...acc, [f.key]: f.value ?? "" }), {})
    );
    setFilterOpen(true);
  };

  const applyFilterDialog = () => {
    if (hasFilterTabs && draftFilter !== filterTabs.activeFilter) {
      filterTabs.onChange?.(draftFilter);
    }
    searchFields.forEach((f) => {
      const next = draftValues[f.key] ?? "";
      if (next !== (f.value ?? "")) f.onChange?.(next);
    });
    setFilterOpen(false);
  };

  const clearButton = onClear ? (
    <Button
      type="button"
      onClick={onClear}
      startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
      sx={{
        textTransform: "none",
        fontWeight: 600,
        fontSize: "0.8125rem",
        color: COLORS.slate600,
        border: `1px solid ${COLORS.slate200}`,
        borderRadius: 2,
        px: 1.75,
        "&:hover": {
          color: COLORS.errorDark,
          borderColor: COLORS.errorDark,
          bgcolor: COLORS.errorLightest,
        },
      }}
    >
      Temizle
    </Button>
  ) : null;

  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 1.25, sm: 2 },
        borderBottom: `1px solid ${COLORS.slate200}`,
        bgcolor: COLORS.slate50,
        flexShrink: 0,
      }}
    >
      {/* 1. satır: başlık — sağda sayaç + aksiyonlar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {icon ? (
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              bgcolor: alpha(palette.primary, 0.1),
              color: palette.primary,
            }}
          >
            {icon}
          </Box>
        ) : null}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          {title ? (
            <Typography
              sx={{ fontWeight: 700, fontSize: { xs: "0.9375rem", sm: "1.0625rem" }, color: COLORS.slate800 }}
              noWrap
            >
              {title}
            </Typography>
          ) : null}
          {subtitle ? (
            <Typography
              sx={{ fontSize: "0.75rem", color: COLORS.slate400, display: { xs: "none", sm: "block" } }}
              noWrap
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {count != null ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.625,
                borderRadius: 2,
                bgcolor: alpha(palette.primary, 0.08),
                border: `1px solid ${alpha(palette.primary, 0.16)}`,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: palette.primary }}>
                {isLoading ? <CircularProgress size={12} sx={{ color: palette.primary }} /> : count}
              </Typography>
              {countLabel ? (
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: palette.primary, display: { xs: "none", sm: "block" } }}>
                  {countLabel}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {isMobile ? (
            <>
              {hasFilterContent ? (
                <IconButton
                  aria-label="Filtrele"
                  onClick={openFilterDialog}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    border: `1px solid ${COLORS.slate200}`,
                    bgcolor: COLORS.white,
                    color: COLORS.slate600,
                  }}
                >
                  <Badge color="primary" variant="dot" invisible={!hasActiveFilters}>
                    <FilterListIcon sx={{ fontSize: 18 }} />
                  </Badge>
                </IconButton>
              ) : null}

              {actionItems.length > 0 ? (
                <>
                  <IconButton
                    aria-label="İşlemler"
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      border: `1px solid ${COLORS.slate200}`,
                      bgcolor: COLORS.white,
                      color: COLORS.slate600,
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                  >
                    {actionItems.map((item) => (
                      <MenuItem
                        key={item.label}
                        disabled={item.disabled}
                        onClick={() => {
                          setMenuAnchor(null);
                          item.onClick?.();
                        }}
                        sx={{ gap: 1.25, fontSize: "0.875rem", fontWeight: 600 }}
                      >
                        {item.icon || null}
                        {item.mobileLabel || item.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ) : null}
            </>
          ) : (
            actionItems.map((item) => (
              <Button
                key={item.label}
                onClick={item.onClick}
                disabled={item.disabled}
                startIcon={item.icon || null}
                variant={item.variant === "primary" ? "contained" : "outlined"}
                sx={
                  item.variant === "primary"
                    ? {
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 2,
                        bgcolor: palette.primary,
                        color: palette.contrastText,
                        boxShadow: `0 6px 16px ${palette.shadow}`,
                        "&:hover": { bgcolor: palette.primaryDark },
                      }
                    : {
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 2,
                        color: COLORS.slate600,
                        borderColor: COLORS.slate200,
                      }
                }
              >
                {item.label}
              </Button>
            ))
          )}
        </Box>
      </Box>

      {/* Mobil: uygulanmış filtre chip'leri */}
      {isMobile && hasActiveFilters ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
          {activeFilterDef ? (
            <Chip
              size="small"
              label={activeFilterDef.label}
              onDelete={() => filterTabs.onChange?.(defaultFilterId)}
              deleteIcon={<CloseIcon titleAccess={`${activeFilterDef.label} filtresini kaldır`} />}
              sx={{ bgcolor: palette.primary, color: palette.contrastText, fontWeight: 600 }}
            />
          ) : null}
          {activeSearchFields.map((f) => (
            <Chip
              key={f.key}
              size="small"
              label={`${f.placeholder}: ${optionLabel(f)}`}
              onDelete={() => f.onChange?.("")}
              deleteIcon={<CloseIcon titleAccess={`${f.placeholder} filtresini kaldır`} />}
              sx={{
                bgcolor: alpha(palette.primary, 0.1),
                color: palette.primary,
                fontWeight: 600,
              }}
            />
          ))}
        </Box>
      ) : null}

      {/* Masaüstü: 2. satır filtre sekmeleri, 3. satır arama */}
      {!isMobile && hasFilterTabs ? (
        <Box sx={{ mt: 1.75 }}>
          <FilterTabs {...filterTabs} />
        </Box>
      ) : null}

      {!isMobile && (hasSearch || clearButton) ? (
        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {searchFields.map((f) =>
            f.type === "select" ? (
              <ListSelectField
                key={f.key}
                placeholder={f.placeholder}
                options={f.options}
                value={f.value}
                onChange={f.onChange}
                sx={f.sx}
              />
            ) : (
              <ListSearchInput
                key={f.key}
                placeholder={f.placeholder}
                value={f.value}
                onChange={f.onChange}
                isPending={f.isPending}
                sx={f.sx}
              />
            )
          )}
          {clearButton ? <Box sx={{ ml: "auto" }}>{clearButton}</Box> : null}
        </Box>
      ) : null}

      {/* Mobil filtre modalı — taslak state */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 700, fontSize: "1rem" }}>
          Filtrele
          <IconButton aria-label="Kapat" onClick={() => setFilterOpen(false)} size="small">
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 3 }}>
          {hasFilterTabs ? (
            <FilterTabs
              filters={filterTabs.filters}
              activeFilter={draftFilter}
              onChange={setDraftFilter}
            />
          ) : null}

          {searchFields.map((f) =>
            f.type === "select" ? (
              <ListSelectField
                key={f.key}
                placeholder={f.placeholder}
                options={f.options}
                value={draftValues[f.key] ?? ""}
                onChange={(v) => setDraftValues((prev) => ({ ...prev, [f.key]: v }))}
                sx={{ width: "100%" }}
              />
            ) : (
              <ListSearchInput
                key={f.key}
                placeholder={f.placeholder}
                value={draftValues[f.key] ?? ""}
                onChange={(v) => setDraftValues((prev) => ({ ...prev, [f.key]: v }))}
                sx={{ width: "100%" }}
              />
            )
          )}

          <Box sx={{ display: "flex", gap: 1.25, mt: 0.5 }}>
            {onClear ? (
              <Button
                fullWidth
                onClick={() => {
                  onClear();
                  setFilterOpen(false);
                }}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, bgcolor: COLORS.slate100, color: COLORS.slate600 }}
              >
                Temizle
              </Button>
            ) : null}
            <Button
              fullWidth
              onClick={applyFilterDialog}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: palette.primary,
                color: palette.contrastText,
                "&:hover": { bgcolor: palette.primaryDark },
              }}
            >
              Uygula
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
